import { Component, OnInit, ViewChild, ElementRef, OnDestroy, Input, ChangeDetectionStrategy, signal, AfterViewInit, computed, effect } from '@angular/core';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { Subscription } from 'rxjs';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { ChangeSelectedTubular } from 'src/app/shared/models/mediator-messages.model';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Tubular } from '../well-configuration/models';
import { saveAs } from 'file-saver-es';
import { Design } from 'src/app/shared/models/design.model';
import { WellSchematicService } from '../../services/well-schematic.service';
import { WellTypeService } from '../../services/well-type-datums.service';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';
import { WellSchematicUi } from './models/well-schematic-models';
import { WellType } from 'src/app/shared/models/wellType.model';
import { SessionStorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Store } from '@ngneat/elf';
import { Subject } from 'rxjs';
import * as ViewBoxHelpers from './helpers/viewbox.helper';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { convertSvgToPngBlob, copyBlobToClipboard, supportsImageClipboard, copySchematicToClipboard } from 'src/app/shared/helpers/schematic-export.helper';


@Component({
    selector: 'app-well-schematic',
    templateUrl: './well-schematic.component.html',
    styleUrls: ['./well-schematic.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WellSchematicComponent implements OnInit, AfterViewInit, OnDestroy {

  // Constants
  private readonly TOOLBAR_HEIGHT = 52;
  private readonly BOTTOM_LABEL_BUFFER = 15; // Extra space to prevent bottom labels from being trimmed

  private _design!: Design;
  private _subscriptions: Subscription;
  private _destroy$ = new Subject<void>();
  private _drawSchematic$ = new Subject<void>();
  private _resizeObserver: ResizeObserver | null = null;
  private _svgEventListeners: Array<{ element: Element; event: string; handler: EventListener }> = [];

  public isPanning = signal(false);
  public isLoading = signal(true);
  public selectedTubularId: string | null = null;
  public wellType = signal<WellType | null>(null);
  private previousWellType: string | null = null;
  public svgContent = signal<string>('');

  public schematicOptions = signal<{ label: string; value: string }[]>([]);
  public selectedSchematicOptions = signal<string[]>([]);

  // SVG pan/zoom state
  private svgElement!: SVGSVGElement;
  private viewBox: ViewBoxHelpers.ViewBox = { x: 0, y: 0, width: 800, height: 1000 };
  private originalViewBox: ViewBoxHelpers.ViewBox = { x: 0, y: 0, width: 800, height: 1000 };
  private scale = 1;
  private panStart: ViewBoxHelpers.Point = { x: 0, y: 0 };

  @Input()
  public isSidebarComp?: boolean = false;

  @ViewChild('wellSchematic', { static: true })
  public wellSchematic!: ElementRef;

  @ViewChild('schematicCm', { static: false })
  public schematicCm!: ContextMenu;

  public readonly schematicContextMenuItems = signal<MenuItem[]>([
    {
      label: 'Copy schematic as image',
      icon: PrimeIcons.COPY,
      command: () => void this.copySchematicAsImage(),
    }
  ]);

  // State Management
  private _componentId!: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.wellSchematicStore = this._storeService.createStore(this.componentId, new WellSchematicUi);
    this.currentStageOnly.set(this.wellSchematicStore.state.currentStageOnly);
    this.drawNoLabels.set(this.wellSchematicStore.state.drawNoLabels);
    this.drawWater.set(this.wellSchematicStore.state.drawWater);
  }
  get componentId(): string {
    return this._componentId;
  }
  public wellSchematicStore!: Store;

  // Display options as signals (synced with store)
  public currentStageOnly = signal(false);
  public drawNoLabels = signal(false);
  public drawWater = signal(false);

  // Computed toolbar height based on visibility
  public toolbarHeight = computed(() => 
    this.wellSchematicStore && this.wellType() ? this.TOOLBAR_HEIGHT : 0
  );

  constructor(
    private _signalRService: SignalRService,
    private _messenger: MediatorService,
    private _wellSchematicService: WellSchematicService,
    private _wellTypeService: WellTypeService,
    private _storeService: StoreService,
    private _elementRef: ElementRef,
    private _toaster: AppNotificationService
  ) {
    this._subscriptions = new Subscription();
    
    // Debounced drawing - waits 500ms after last trigger
    this._subscriptions.add(
      this._drawSchematic$.pipe(
        debounceTime(500),
        takeUntil(this._destroy$)
      ).subscribe(() => this.performDraw())
    );

    // Watch svgContent signal and attach listeners when it changes
    effect(() => {
      const svg = this.svgContent();
      if (svg) {
        // Use queueMicrotask to defer until after current rendering completes
        queueMicrotask(() => this.attachSvgEventListeners());
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this._storeService.watchSession<Design>(SessionStorageKeys.DESIGN)
      .pipe(takeUntil(this._destroy$))
      .subscribe(design => {
        if (design?.id !== this._design?.id) {
          // Design changed, clear wellType and reset tracking
          this.wellType.set(null);
          this.previousWellType = null;
          if (this.isSidebarComp && design) {
            this.wellSchematicStore = this._storeService.createStore('well-schematic-sidebar@' + design.id, new WellSchematicUi);
            this.currentStageOnly.set(this.wellSchematicStore.state.currentStageOnly);
            this.drawNoLabels.set(this.wellSchematicStore.state.drawNoLabels);
            this.drawWater.set(this.wellSchematicStore.state.drawWater);
          }
        }
        if (design) {
          this._design = design;
          this.loadWellTypeAndDraw();
        }
    });

    // Watch for tubular string changes
    this._storeService.watchSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS)
      .pipe(takeUntil(this._destroy$))
      .subscribe(appIds => {
        if (appIds?.tubularId !== undefined && this._design) {
          // Tubular string changed, request new SVG
          this.requestDraw();
        }
      });
    
    // Use scoped SignalR subscription
    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  ngAfterViewInit(): void {
    // Observe the host element (component root) for accurate GridStack dimensions
    // In GridStack's position:absolute context, the host element is what gets sized
    this._resizeObserver = new ResizeObserver(() => this.onContainerResize());
    this._resizeObserver.observe(this._elementRef.nativeElement);
  }

  private onContainerResize(): void {
    // Immediately re-fit existing SVG content for instant feedback
    if (this.svgElement && this.svgContent()) {
      this.resetView();
    }
    // Then request new SVG from backend at new dimensions (debounced)
    this.requestDraw();
  }

  signalRfunc(data: any) {
    if (data.designId != this._design.id) {
      return;
    }
    if (data.action == PeriforOnChangeMessages.REFRESH_SCHEMATIC || data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS || data.action == PeriforOnChangeMessages.REFRESH_GLV) {
      this.loadWellTypeAndDraw();
    }
  }

  loadWellTypeAndDraw() {
    this.isLoading.set(true);
    
    this._wellTypeService.getWellType(true).pipe(takeUntil(this._destroy$)).subscribe({
      next: (wellType) => {
        // If well type changed, reset drawWater to match new well type default
        const wellTypeChanged = this.previousWellType && this.previousWellType !== wellType.type;
        const isFirstLoad = !this.previousWellType;
        
        this.wellType.set(wellType);
        this.previousWellType = wellType.type;
        
        // Initialize drawWater based on well type on first load or when well type changes
        if (isFirstLoad || wellTypeChanged) {
          const newDrawWater = wellType.type === 'Platform';
          this.drawWater.set(newDrawWater);
          this.wellSchematicStore.update((state) => ({
            ...state,
            drawWater: newDrawWater
          }));
        }
        this.buildSchematicOptions();
        this.syncSelectedOptions();
        // Use requestDraw (debounced) rather than performDraw directly.
        // Reason: wellType.set() triggers Angular CD which renders the toolbar,
        // which shrinks schematic-container height. The ResizeObserver then fires
        // with the correct (post-toolbar) height and resets the debounce.
        // Calling performDraw() immediately would use the pre-toolbar height → black gap at top.
        this.requestDraw();
      }
    });
  }

  // Public API for external components to trigger a redraw
  public drawSchematic() {
    this.requestDraw();
  }

  // Request a debounced draw (for resize, toggles, etc.)
  private requestDraw() {
    this._drawSchematic$.next();
  }

  // Draw — measure the GridStack wrapper for accurate dimensions.
  // GridStack's grid-stack-item-content is the actual constrained container.
  private performDraw() {
    if (!this._design?.id) return;

    const hostElement = this._elementRef.nativeElement as HTMLElement;
    const gridStackWrapper = hostElement.closest('.grid-stack-item-content') as HTMLElement;
    
    // Use GridStack wrapper if available (dashboard context), otherwise use host
    const measureFrom = gridStackWrapper || hostElement;
    const rect = measureFrom.getBoundingClientRect();
    
    const width = Math.floor(rect.width) || 500;
    const height = Math.floor(rect.height - this.toolbarHeight() - this.BOTTOM_LABEL_BUFFER) || 500;

    if (width <= 0 || height <= 0) return;

    this.isLoading.set(true);

    const drawLabels = !this.drawNoLabels();
    const drawReferenceLines = this.drawWater();
    const drawCurrentSection = this.currentStageOnly();

    // Note: When drawCurrentSection is true, the backend should show the current tubular string
    // AND all previous strings (based on sequence number). The backend determines the "current"
    // tubular from user state and should display all strings with sequenceNumber <= current.
    // If only the current string is shown without previous strings, this is a backend issue.
    
    this._wellSchematicService.getSvg(this._design.id, width, height, drawLabels, drawReferenceLines, drawCurrentSection, this.selectedTubularId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (svg) => {
          if (svg) {
            this.svgContent.set(svg);
            this.isLoading.set(false);
            // Effect in constructor will attach listeners when svgContent changes
          }
        }
      });
  }

  private attachSvgEventListeners() {
    // Clean up previous event listeners
    this.removeSvgEventListeners();

    const svgWrapper = this.wellSchematic.nativeElement.querySelector('.svg-wrapper');
    if (!svgWrapper) return;

    this.svgElement = svgWrapper.querySelector('svg') as SVGSVGElement;
    if (!this.svgElement) return;

    // Force SVG to center content and scale uniformly
    this.svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    // Store original viewBox for reset
    const viewBoxAttr = this.svgElement.getAttribute('viewBox');
    if (viewBoxAttr) {
      const parsed = ViewBoxHelpers.parseViewBoxString(viewBoxAttr);
      if (parsed) {
        this.viewBox = parsed;
        this.originalViewBox = parsed;
        this.scale = 1;
        
        // Auto-fit content on initial load so schematic displays at proper zoom level
        const fitViewBox = this.computeContentFittedViewBox();
        if (fitViewBox) {
          this.viewBox = fitViewBox;
        }
        this.updateViewBox();
      }
    }

    // Find all clickable tubular elements using data attribute
    const clickableElements = this.svgElement.querySelectorAll('[data-tubular-id]');
    
    clickableElements.forEach((element: Element) => {
      (element as SVGElement).style.cursor = 'pointer';
      
      const dblclickHandler = (event: Event) => {
        event.stopPropagation();
        const tubularId = (element as SVGElement).getAttribute('data-tubular-id');
        this.selectTubular(tubularId);
      };
      element.addEventListener('dblclick', dblclickHandler);
      this._svgEventListeners.push({ element, event: 'dblclick', handler: dblclickHandler });
    });

    // Click on background to deselect
    const clickHandler = () => {
      this.clearSelection();
    };
    this.svgElement.addEventListener('click', clickHandler);
    this._svgEventListeners.push({ element: this.svgElement, event: 'click', handler: clickHandler });
  }

  private removeSvgEventListeners() {
    this._svgEventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this._svgEventListeners = [];
  }

  private selectTubular(tubularId: string | null) {
    if (!tubularId) return;

    // Clear previous selection
    this.clearSelection();

    // Store selected ID
    this.selectedTubularId = tubularId;

    // Call the backend for a redraw with the selected tubular highlighted
    this.drawSchematic();

    // Publish selection message with just the ID
    this._messenger.publish(new ChangeSelectedTubular(new Tubular({ id: tubularId } as any)));
  }

  private clearSelection() {
    this.selectedTubularId = null;
    
    // Remove selection class from all elements
    if (this.svgElement) {
      const allSelected = this.svgElement.querySelectorAll('.selected');
      allSelected.forEach(el => el.classList.remove('selected'));
    }
  }

  async onExportPng(): Promise<void> {
    if (!this.svgElement) return;

    const blob = await convertSvgToPngBlob(this.svgElement);
    if (blob) {
      saveAs(blob, `${this?._design.name}_schematic.png`);
    }
  }

  public onSchematicContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.schematicCm?.show(event);
  }

  public async copySchematicAsImage(): Promise<void> {
    const result = await copySchematicToClipboard(this.svgElement);
    if (result.success) {
      this._toaster.showSuccess(result.message);
    } else {
      this._toaster.showError(result.message);
    }
  }

  private buildSchematicOptions() {
    const wellTypeValue = this.wellType();
    const options = [
      { label: 'Current Stage', value: 'currentStageOnly' },
      { label: 'No Labels', value: 'drawNoLabels' }
    ];
    if (wellTypeValue?.type !== 'Land') {
      options.push({ label: 'MSL', value: 'drawWater' });
    }
    this.schematicOptions.set(options);
  }

  private syncSelectedOptions() {
    const selected: string[] = [];
    if (this.currentStageOnly()) selected.push('currentStageOnly');
    if (this.drawNoLabels()) selected.push('drawNoLabels');
    if (this.drawWater()) selected.push('drawWater');
    this.selectedSchematicOptions.set(selected);
  }

  onSchematicOptionsChange(selectedOptions: string[]) {
    this.selectedSchematicOptions.set(selectedOptions);
    
    const newCurrentStageOnly = selectedOptions.includes('currentStageOnly');
    const newDrawNoLabels = selectedOptions.includes('drawNoLabels');
    const newDrawWater = selectedOptions.includes('drawWater');
    
    this.currentStageOnly.set(newCurrentStageOnly);
    this.drawNoLabels.set(newDrawNoLabels);
    this.drawWater.set(newDrawWater);
    
    this.wellSchematicStore.update((state) => ({
      ...state,
      currentStageOnly: newCurrentStageOnly,
      drawNoLabels: newDrawNoLabels,
      drawWater: newDrawWater
    }));
    this.requestDraw();
  }

  onMouseDown = (event: MouseEvent) => { 
    this.isPanning.set(true);
    this.panStart = { x: event.clientX, y: event.clientY };
  }

  onMouseUp = () => {
    this.isPanning.set(false);
  }

  onMouseMove(event: MouseEvent) {
    if (this.isPanning() && this.svgElement) {
      const currentPosition = { x: event.clientX, y: event.clientY };
      this.viewBox = ViewBoxHelpers.computePanViewBox(this.viewBox, this.panStart, currentPosition, this.scale);
      this.updateViewBox();
      
      this.panStart = currentPosition;
      event.preventDefault();
    }
  }

  onMouseWheel(event: WheelEvent) {
    if (!this.svgElement) return;

    event.preventDefault();

    if (event.shiftKey) {
      // Pan with shift+scroll
      this.viewBox = ViewBoxHelpers.computeShiftPanViewBox(this.viewBox, event.deltaX, event.deltaY, this.scale);
      this.updateViewBox();
    } else {
      // Zoom around cursor
      const rect = this.svgElement.getBoundingClientRect();
      const mousePosition = { x: event.clientX, y: event.clientY };
      
      this.viewBox = ViewBoxHelpers.computeZoomViewBox(this.viewBox, mousePosition, rect, event.deltaY);
      this.scale = ViewBoxHelpers.computeNewScale(this.scale, event.deltaY);
      this.updateViewBox();
    }
  }

  resetView() {
    if (!this.svgElement || !this.originalViewBox) return;

    // Prefer centering to live SVG content bounds so reset works consistently
    // across container modes (dashboard widget vs tool window sidebar).
    const fitViewBox = this.computeContentFittedViewBox();
    this.viewBox = fitViewBox ?? { ...this.originalViewBox };
    this.scale = 1;
    this.updateViewBox();
  }

  private computeContentFittedViewBox(): ViewBoxHelpers.ViewBox | null {
    if (!this.svgElement) return null;

    let bbox: DOMRect;
    try {
      bbox = this.svgElement.getBBox();
    } catch {
      return null;
    }

    const padding = 15; // Fixed pixel padding for comfortable spacing
    return ViewBoxHelpers.computeContentFittedViewBox(bbox, padding);
  }

  private updateViewBox() {
    if (this.svgElement) {
      this.svgElement.setAttribute('viewBox', ViewBoxHelpers.formatViewBoxString(this.viewBox));
    }
  }

  ngOnDestroy() {
    this.removeSvgEventListeners();
    this._resizeObserver?.disconnect();
    this._subscriptions?.unsubscribe();
    this._destroy$.next();
    this._destroy$.complete();
  }

}
